import React, { useState, useEffect, useRef } from 'react';
import './PartnerSelectionChat.css';

const PartnerSelectionChat = ({ 
  isOpen, 
  onClose, 
  generatedImages: preGeneratedImages,
  onPartnerSelected,
  currentUser
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState(preGeneratedImages || []);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [error, setError] = useState(null);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const messagesEndRef = useRef(null);

  // Update generated images when props change
  useEffect(() => {
    if (preGeneratedImages && preGeneratedImages.length > 0) {
      setGeneratedImages(preGeneratedImages);
    }
  }, [preGeneratedImages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [generatedImages]);

  // Disable main page scroll and hide navbar when chatbot is open
  useEffect(() => {
    if (isOpen) {
      // Store the current scroll position
      const scrollY = window.scrollY;
      
      // Disable scroll by setting body to fixed position
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      // Hide navbar
      document.body.classList.add('chatbot-open');
      
      return () => {
        // Re-enable scroll when chatbot closes
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        
        // Show navbar
        document.body.classList.remove('chatbot-open');
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Keyboard detection for mobile
  useEffect(() => {
    if (!isOpen) return;

    let initialHeight = window.innerHeight;

    const handleResize = () => {
      const currentHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      
      // If height decreased significantly, keyboard is likely visible
      const heightDifference = initialHeight - currentHeight;
      const keyboardVisible = heightDifference > 150; // Threshold for keyboard detection
      
      setIsKeyboardVisible(keyboardVisible);
    };

    // Listen for viewport changes
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    // Initial check
    handleResize();

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, [isOpen]);

  const handleImageSelect = (imageIndex) => {
    setSelectedImageIndex(imageIndex);
  };

  const handleConfirmSelection = () => {
    if (selectedImageIndex !== null && generatedImages[selectedImageIndex]) {
      const selectedImage = generatedImages[selectedImageIndex];
      onPartnerSelected(selectedImage);
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedImageIndex(null);
    setError(null);
  };

  return (
    <>
    {isOpen && (
    <div className="partner-selection-overlay" onClick={handleClose}>
      <div className={`partner-selection-container ${isKeyboardVisible ? 'keyboard-visible' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="partner-selection-header">
          <div className="partner-selection-title">
            <div className="partner-selection-avatar">
              <img src="/heartintel.svg" alt="LUV" className="partner-selection-logo" />
            </div>
            <div>
              <h3>Choose Your Partner</h3>
              <p>Select the image that best represents your ideal partner</p>
            </div>
          </div>
          <button className="partner-selection-close" onClick={handleClose}>×</button>
        </div>

        <div className="partner-selection-content">
          {isGenerating && (
            <div className="generating-message">
              <div className="generating-spinner">
                <div className="spinner"></div>
              </div>
              <p>Creating your ideal partner images...</p>
              <p className="generating-subtext">This may take a moment</p>
            </div>
          )}

          {error && (
            <div className="error-message">
              <div className="error-icon">⚠️</div>
              <p>{error}</p>
            </div>
          )}

          {generatedImages.length > 0 && !isGenerating && (
            <div className="image-selection-container">
              <div className="selection-instructions">
                <h4>Tap to select your favorite</h4>
                <p>Choose the image that best represents your ideal partner</p>
              </div>
              
              <div className="image-grid">
                {generatedImages.map((imageData, index) => (
                  <div 
                    key={index}
                    className={`image-option ${selectedImageIndex === index ? 'selected' : ''} ${!imageData.success ? 'error' : ''}`}
                    onClick={() => imageData.success && handleImageSelect(index)}
                  >
                    {imageData.success ? (
                      <>
                        <img 
                          src={imageData.url} 
                          alt={`Partner option ${index + 1}`}
                          className="partner-image"
                          onLoad={() => console.log(`Image ${index + 1} loaded successfully:`, imageData.url)}
                          onError={(e) => console.error(`Image ${index + 1} failed to load:`, imageData.url, e)}
                        />
                        <div className="image-overlay">
                          <div className="selection-indicator">
                            {selectedImageIndex === index ? '✓' : ''}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="image-error">
                        <div className="error-icon">⚠️</div>
                        <p>Failed to generate</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="selection-actions">
                <button 
                  className={`confirm-button ${selectedImageIndex !== null ? 'enabled' : 'disabled'}`}
                  onClick={handleConfirmSelection}
                  disabled={selectedImageIndex === null}
                >
                  {selectedImageIndex !== null ? 'Confirm Selection' : 'Select an Image'}
                </button>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
    )}

    </>
  );
};

export default PartnerSelectionChat;
